import HologardeLogo from '@assets/hologarde-logo.png';
import { ReactComponent as HolosafeLogo } from '@assets/hologarde-safe.svg';
import { Box, HStack, Icon, Image } from '@chakra-ui/react';
import CenterMapControl from '@components/map/controls/CenterMapControl';
import MapSidebar from '@components/map/controls/mapSidebar/MapSidebar';
import { ToolMenu } from '@components/map/controls/menus/ToolMenu';
import { PitchAndBearingControl } from '@components/map/controls/PitchAndBearingControl';
import ScreenshotControl from '@components/map/controls/ScreenshotControl';
import ZoomControl from '@components/map/controls/ZoomControl';
import { LADMap } from '@components/map/LADMap';
import { LiveMap } from '@components/map/LiveMap';
import { MapContextProvider } from '@components/map/MapContext';
import { ReplayMap } from '@components/map/ReplayMap';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  selectActiveScreenConfigurationMapZoom,
  selectActiveScreenConfigurationPosition,
} from '@redux/config/config.selectors';
import { issueMapCommand } from '@redux/maps/mapsCommands.reducer';
import { AUTOMATON_SECTION_LAYER_NAMES } from '@utils/map/map.constants';
import { motion } from 'framer-motion';
import { isEqual } from 'lodash';
import { FC, useEffect, useRef } from 'react';

import { LayerNameEnum, MapIdEnum, OperationalMapId } from '@/types/map.types';

import { StatisticsMap } from './StatisticsMap';

const INFO_LAYERS: LayerNameEnum[] = [
  LayerNameEnum.FLIGHT_PLAN_FILLS,
  LayerNameEnum.FLIGHT_PLAN_OUTLINES,
  LayerNameEnum.PERIMETER_CAMERA_LABELS,
  LayerNameEnum.PERIMETER_CAMERA_SECTOR_FILLS,
  ...AUTOMATON_SECTION_LAYER_NAMES,
  LayerNameEnum.PERIMETER_SENSOR_ICONS,
  LayerNameEnum.PERIMETER_SENSOR_LABELS,
  LayerNameEnum.SENSOR_ICONS,
  LayerNameEnum.SENSOR_LABELS,
  LayerNameEnum.STROBE_SECTORS,
  LayerNameEnum.POLYGON_LOCATIONS,
  LayerNameEnum.POLYGON_LOCATION_OUTLINES,
  LayerNameEnum.TARGETS,
];

const MAP_COMPONENTS: Record<OperationalMapId, FC> = {
  [MapIdEnum.LIVE]: LiveMap,
  [MapIdEnum.REPLAY]: ReplayMap,
  [MapIdEnum.LAD]: LADMap,
  [MapIdEnum.STATISTICS]: StatisticsMap,
};

interface MapWrapperProps {
  selectedMap: OperationalMapId;
}

export function MapWrapper({ selectedMap }: Readonly<MapWrapperProps>) {
  const mapConstraintsRef = useRef<HTMLDivElement>(null);
  const centerMapView = useWithDispatch(issueMapCommand);

  const MapComponent = MAP_COMPONENTS[selectedMap];

  const activeGlobalScreenConfigPosition = useAppSelector(selectActiveScreenConfigurationPosition, isEqual);
  const activeGlobalScreenConfigMapZoom = useAppSelector(selectActiveScreenConfigurationMapZoom, isEqual);

  useEffect(() => {
    if (activeGlobalScreenConfigPosition && activeGlobalScreenConfigMapZoom) {
      centerMapView({
        mapId: selectedMap,
        command: {
          type: 'CENTER',
          payload: { center: activeGlobalScreenConfigPosition, zoom: activeGlobalScreenConfigMapZoom },
        },
      });
    }
  }, [activeGlobalScreenConfigPosition, activeGlobalScreenConfigMapZoom, centerMapView, selectedMap]);

  return (
    <MapContextProvider
      mapConstraintsRef={mapConstraintsRef}
      mapId={selectedMap}
      infoLayers={INFO_LAYERS}
      center={activeGlobalScreenConfigPosition}
    >
      <HStack height="100%" width="100%" gap={0} padding={0}>
        <Box height="100%" width="100%" zIndex={1} paddingLeft={7} paddingRight="76px">
          <motion.div
            ref={mapConstraintsRef}
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'neutral.900',
            }}
          >
            <MapComponent />
          </motion.div>
        </Box>
        <MapSidebar paddingBottom={3}>
          <ToolMenu />
          <Icon as={HolosafeLogo} width="30px" height="26px" justifySelf="flex-end" alignSelf="center" />
        </MapSidebar>
        <MapSidebar position="right" paddingBottom={2}>
          <ZoomControl />
          <ScreenshotControl />
          <CenterMapControl />
          <PitchAndBearingControl />
        </MapSidebar>
        <Image
          src={HologardeLogo}
          userSelect="none"
          zIndex={1}
          position="absolute"
          boxSize={{ base: '96px', '4k': '192px' }}
          top="standardS"
          right="standardS"
          transform="translateX(-74px)"
          pointerEvents="none"
        />
      </HStack>
    </MapContextProvider>
  );
}
